/*
 * Copyright (c)  2024 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import range from 'lodash/range';
import {translate} from '../locale/locale';

const DIAG_CODE = 'DiagCode';

/**
 * Given a error code returns true if DiagCode is provided otherwise
 * returns false.
 *
 * ```typescript
 *
 *  import {hasDiagCode} from '@ion/core/src/services';
 *
 *  hasDiagCode(error);
 *
 * ```
 */
export const hasDiagCode = (error) => {
  return error?.indexOf?.(DIAG_CODE) > -1;
};

/**
 * Maps and returns string based on StatCode & DiagCode.
 *
 * ```typescript
 *
 *  import {getErrorCode} from '@ion/core/src/services';
 *
 *  getErrorCode(error);
 *
 * ```
 */
export const getErrorCode = ({
  error = '',
  appId = null,
  statCode = [],
  diagCode = [],
  diagCode32Bit = {}
} = {}) => {
  return translate('downloadjobs:failureReason', {
    statcode : getStatCodeDescription(error, statCode),
    diagCode : getDiagCodeDescription({errorCode: error, appId, diagCode, diagCode32Bit}),
    diagMsg  : getDiagMessage(error)
  });
};

/**
 * Utility which returns StatCode, DiagCode and DiagCode32Bit mappings.
 *
 * ```typescript
 *
 *  import {getCodeMappings} from '@ion/core/src/services';
 *
 *  getCodeMappings();
 *
 * ```
 */
export const getCodeMappings = () => {
  const {
    statCode,
    diagCode,
    diagCode32Bit
  } = translate('downloadjobs:statusMappings', {returnObjects: true});
  return {
    statCode,
    diagCode,
    diagCode32Bit
  };
};

/**
 * Maps and returns array based on StatCode & DiagCode.
 *
 * ```typescript
 *
 *  import {hasErrorCodes} from '@ion/core/src/services';
 *
 *  hasErrorCodes(items);
 *
 * ```
 */
export const hasErrorCodes = (items, appId) => {
  const {
    statCode,
    diagCode,
    diagCode32Bit
  } = getCodeMappings();
  return items?.map?.(item => {
    if (hasDiagCode(item)) {
      return getErrorCode({
        error : item,
        appId : appId,
        statCode,
        diagCode,
        diagCode32Bit
      });
    }
    return item;
  });
};

/**
 * Utility which extracts StatCode value and maps its
 * corresponding value.
 *
 * ```typescript
 *
 *  import {getStatCodeDescription} from '@ion/core/src/services';
 *
 *  getStatCodeDescription(errorCode);
 *
 * ```
 */
export const getStatCodeDescription = (errorCode: string, statCode: string[]) => {
  const code = extractValues(errorCode);
  if (isNil(code)) {
    return 'null';
  }
  return statCode?.[code?.[0]];
};

/**
 * Utility which extracts DiagCode and DiagCode32Bit value and
 * maps its corresponding value.
 *
 * ```typescript
 *
 *  import {getDiagCodeDescription} from '@ion/core/src/services';
 *
 *  getDiagCodeDescription(errorCode);
 *
 * ```
 */
export const getDiagCodeDescription = ({
  errorCode = '',
  appId = null,
  diagCode = [],
  diagCode32Bit = {}
}) => {
  const code       = extractValues(errorCode);
  const error      = code?.[1];
  const errorCodes = range(29, 34);
  if (isNil(error)) {
    return 'null';
  }
  if (appId < 1000 && (errorCodes?.indexOf(error) > -1)) {
    return diagCode32Bit?.[error];
  }
  return diagCode?.[error];
};

/**
 * Utility which extracts DiagMessage.
 *
 * ```typescript
 *
 *  import {getDiagMessage} from '@ion/core/src/services';
 *
 *  getDiagMessage(errorCode);
 *
 * ```
 */
export const getDiagMessage = (error) => {
  return error?.match?.(/DiagMsg:\s*(.+)/)?.[1] || '';
};

/**
 * Given a string extracts and returns number.
 *
 * ```typescript
 *
 *  import {extractValues} from '@ion/core/src/services';
 *
 *  extractValues(errorCode);
 *
 * ```
 */
export const extractValues = (errorCode) => {
  const regex = /\d+/g;
  return errorCode?.match?.(regex)?.map?.(Number);
};
